import { Box, Button, DialogActions, DialogContent, FormControl, FormControlLabel, FormLabel, Grid, Radio, RadioGroup, Stack, TextField, Typography } from "@mui/material";
import { Fragment, useEffect, useState } from "react";
import useOrderDetail from "src/hooks/orders/useOrderDetail";
import { closeSnackbar, enqueueSnackbar } from "notistack";
import useAppGlobal from "src/context/useAppGlobal";
import { CHECK_RETURN_PROD_STATUS, COMMOM_STATUS, EXPORT_TYPE, EXPORT_TYPE_TRANSLATETRANSLATE, orderStatusFormatted, PRINT_SIZE } from "src/constant";
import useOrderPreview from "src/hooks/orders/useOrderPreview";
import DeliveredReceipt from "./export-form/DeliveredReceipt";
import useOrderPrint from "src/hooks/orders/useOrderPrint";
import useProcessLogDetail from "src/hooks/process-log/useProcessLogDetail";
import appConfig from "src/config/env";
import OrderTable from "./OrderTable";
import WaitListTable from "./WaitListTable";
import useDebounce from "src/hooks/useDebounce";
import useOrderScan from "src/hooks/orders/useOrderScan";
import useOrderTrackingStatus from "src/hooks/orders/useOrderTrackingStatus";
import convertCurrency, { convertIntMoney } from "src/helper/convertCurrency";
import ProcessingBadge from "src/components/shared/ProcessingBagde";
import MallScanner from "src/components/shared/ScanningInput";
import useSettingBatchCode from "src/hooks/setting/batch-code/useSettingBatchCode";
import useProductDetail from "src/hooks/product/useProductDetail";
import useOrderCheckReturnCreate from "src/hooks/orders/useOrderCheckReturnCreate";

export default function OrderReturnDialog({ item, check, mutate: parentMutate }) {
  const { closeDialog } = useAppGlobal();
  const [logId, setLogId] = useState();
  const [exportTypePick, setExportTypePick] = useState(() => (check ? EXPORT_TYPE.WarehouseImport : EXPORT_TYPE.DeliveredReceipt));
  const [waitList, setWaitList] = useState([]);
  const [codeDetail, setCodeDetail] = useState();
  const [productState, setProductState] = useState({
    qualityStatus: CHECK_RETURN_PROD_STATUS.normal,
  });
  const [isPrinted, setIsPrinted] = useState();
  const debounceCode = useDebounce(codeDetail?.code, 200);
  const { data: orderDetail } = useOrderDetail(item?._id, {
    warehouse: item?.warehouse,
  });
  const { data: orderPreview, isLoading } = useOrderPreview(item?._id && isPrinted ? item._id : null, {
    warehouse: item?.warehouse,
    outputFile: exportTypePick,
  });
  const { mutate, isLoading: printLoading } = useProcessLogDetail(logId, {
    warehouse: item?.warehouse,
  });
  const { trigger: printAll } = useOrderPrint();
  const { trigger: scan } = useOrderCheckReturnCreate(item?._id);
  const { trigger: changeStatus } = useOrderTrackingStatus(item?._id);
  const { data: batchCode } = useSettingBatchCode();
  const { data: productDetail } = useProductDetail(
    !!item?.product?._id ? item?.product?._id : item?.product,
    !!item?.productModel?._id ? item?.productModel?._id : item?.productModel,
  );

  useEffect(() => {
    if (debounceCode) {
      const refinedArray = debounceCode?.map((v) => v?.replaceAll("Shift", ""));
      if (productDetail?.data?.productGroup?.skipValidItemCode) {
        setWaitList(refinedArray);
      } else {
        if (!Array.isArray(batchCode?.data?.prefixItemCodes) || !Array.isArray(batchCode?.data?.stripItemCodes)) return;
        const validCodes = [];
        const invalidCodes = [];

        refinedArray.forEach((line) => {
          let cleaned = line;

          batchCode.data.stripItemCodes.forEach((strip) => {
            if (cleaned.startsWith(strip + "/")) {
              cleaned = cleaned.replace(strip + "/", "");
            } else if (cleaned.startsWith(strip)) {
              cleaned = cleaned.replace(strip, "");
            }
          });

          const hasValidPrefix = batchCode.data.prefixItemCodes.some((prefix) => cleaned.startsWith(prefix));

          if (hasValidPrefix) {
            validCodes.push(cleaned);
          } else {
            invalidCodes.push(cleaned);
          }
        });

        if (validCodes.length > 0) {
          setWaitList((prev) => [...prev, ...validCodes]);
        }

        if (invalidCodes.length > 0) {
          setCodeDetail((prev) => ({ ...prev, code: null, reason: null }));

          enqueueSnackbar(`Mã không hợp lệ: ${invalidCodes.join(", ")}`, {
            variant: "warning",
          });
        }
      }
      setCodeDetail((prev) => ({ ...prev, code: null, reason: null }));
    }
  }, [debounceCode]);

  useEffect(() => {
    const handleScan = async () => {
      const code = waitList[0];
      if (!code) return;

      const res = await scan({
        body: { ...productState, itemCode: code, orderDetailId: item?._id },
      });

      if (res?.success) {
        setProductState((prev) => ({ ...prev, note: undefined }));
        setWaitList((prev) => prev.slice(1));
      } else {
        enqueueSnackbar(
          <span>
            {res?.error?.message} <b>{code}</b>
          </span>,
          {
            variant: "warning",
            persist: true,
            action: (snackbarId) => (
              <Button
                variant="contained"
                size="small"
                onClick={() => {
                  setWaitList((prev) => prev.slice(1));
                  closeSnackbar(snackbarId);
                }}
              >
                Xác nhận
              </Button>
            ),
          },
        );
      }
    };

    if (waitList.length > 0) {
      handleScan();
    }
  }, [waitList.length]);

  const handleConfirm = async () => {
    const res = await changeStatus({
      body: {
        trackingStatus: check ? orderStatusFormatted.Returned : orderStatusFormatted.ReadyToPick,
      },
    });
    if (res?.success) {
      setIsPrinted(res?.data?._id);
      return enqueueSnackbar("Xác nhận thành công!", { variant: "success" });
    } else {
      return enqueueSnackbar(res?.error?.message, { variant: "error" });
    }
  };

  const handlePrint = async () => {
    const res = await printAll({
      body: {
        orderIds: [item._id],
        pageSize: PRINT_SIZE.a6,
        warehouse: item?.warehouse,
        outputFiles: [exportTypePick],
      },
    });
    if (res?.success) {
      setLogId(res?.data?._id);
      handleGetLog(res?.data?._id);
      return enqueueSnackbar("Tiến hành xuất phiếu...", { variant: "success", className: 'notistack-confirm' });
    } else {
      return enqueueSnackbar(res?.error?.message ?? res?.error?.message, {
        variant: "error",
      });
    }
  };

  const handleGetLog = (logIdParam) => {
    const logIdToUse = logIdParam || logId;
    if (!logIdToUse) return;
    const timeOut = setInterval(async () => {
      try {
        const res = await mutate();
        if (res?.success) {
          if (res.data.status === COMMOM_STATUS.done) {
            if (Array.isArray(res.data?.urls)) {
              res.data.urls.forEach((v) => {
                window.open(`${appConfig.API_FILE}/${v}`, "_blank");
              });
            }
            clearInterval(timeOut);
            closeDialog();
          }

          if (res.data.status === COMMOM_STATUS.error) {
            enqueueSnackbar("Không xuất được đơn", { variant: "error" });
            clearInterval(timeOut);
          }
        }
      } catch (err) {
        clearInterval(timeOut);
      }
    }, 1000);
  };

  const totalProd = orderDetail?.data?.orderDetails?.reduce((acc, curr) => acc + curr?.qty, 0);

  return (
    <Fragment>
      <DialogContent>
        <Grid container>
          {!isPrinted ? (
            <>
              <Grid
                xs={12}
                item
                display="flex"
              >
                {/* INFO */}
                <Box
                  flex={1}
                  display="flex"
                  flexDirection="column"
                  sx={{
                    background: "var(--light-grey)",
                    p: "6px 12px",
                    borderRadius: "6px",
                  }}
                >
                  <Typography
                    sx={{ fontSize: "16px" }}
                    fontWeight="bold"
                  >
                    Thông tin khách hàng
                  </Typography>
                  <Typography
                    mt="6px"
                    variant="caption"
                  >
                    <strong>Tên KH: </strong>
                    {item?.customer?.fullName ?? "N/A"}
                  </Typography>
                  <Typography
                    variant="caption"
                    mt="4px"
                  >
                    <strong>SĐT: </strong>
                    {item?.customer?.phone ?? "N/A"}
                  </Typography>
                  <Typography
                    variant="caption"
                    mt="4px"
                  >
                    <strong>Địa chỉ: </strong>
                    {item?.recipient?.detailAddress ?? "N/A"}
                  </Typography>
                  <Typography
                    variant="caption"
                    mt="4px"
                  >
                    <strong>NV phụ trách: </strong>
                    {item?.sales?.fullName ?? "N/A"}
                  </Typography>
                </Box>
                {/* DETAIL */}
                <Box
                  flex={1}
                  display="flex"
                  flexDirection="column"
                  sx={{
                    background: "var(--light-grey)",
                    p: "6px 12px",
                    borderRadius: "6px",
                  }}
                >
                  <Typography
                    sx={{ fontSize: "16px" }}
                    fontWeight="bold"
                  >
                    Thông tin đơn hàng{" "}
                    <ProcessingBadge
                      value={item?.type === orderStatusFormatted.ReturnRefund ? "Đơn trả" : "Đơn đổi"}
                      asset={{ color: "confirm", text: "confirm" }}
                    />
                  </Typography>
                  <Typography
                    mt="6px"
                    variant="caption"
                  >
                    <strong>Mã đơn hàng: </strong>
                    {item?.code ?? "N/A"}
                  </Typography>
                  <Typography
                    variant="caption"
                    mt="4px"
                  >
                    <strong>Tổng số sản phẩm: </strong>
                    {orderDetail?.data?.orderDetails?.length ?? 0}
                  </Typography>
                  <Typography
                    mt="6px"
                    variant="caption"
                  >
                    <strong>Tổng số lượng sản phẩm: </strong>
                    {totalProd ?? 0}
                  </Typography>
                  <Typography
                    mt="4px"
                    variant="caption"
                  >
                    <strong>Tổng giá trị đơn hàng: </strong>
                    {convertIntMoney(orderDetail?.data?.subTotal ?? 0) ?? 0} đồng
                  </Typography>
                  <Typography variant="caption">({convertCurrency(orderDetail?.data?.subTotal ?? 0)} đồng)</Typography>
                  <Typography
                    mt="4px"
                    variant="caption"
                  >
                    <strong>Ghi chú của đơn hàng: </strong>
                    {orderDetail?.data?.note ?? "N/A"}
                  </Typography>
                </Box>
              </Grid>
              {/* IMPORT PROD */}
              <Grid
                xs={12}
                item
              >
                {/* PRO TABLE */}
                <Grid
                  container
                  spacing={2}
                >
                  <Grid
                    xs={8}
                    item
                  >
                    <Stack
                      gap={2}
                      mt={1}
                    >
                      <Typography
                        fontWeight="bold"
                        fontSize="16px"
                      >
                        Danh sách sản phẩm
                      </Typography>
                      <OrderTable
                        prodList={orderDetail?.data?.orderDetails}
                        maxHeight="442px"
                        prodReturn
                        total={orderDetail?.data?.totalQty}
                        pickedTotal={orderDetail?.data?.totalPickedQty}
                      />
                    </Stack>
                  </Grid>
                  <Grid
                    xs={4}
                    item
                  >
                    <Stack
                      gap={2}
                      mt={1}
                    >
                      <Typography
                        fontWeight="bold"
                        sx={{ fontSize: "16px" }}
                      >
                        Scan mã sản phẩm
                      </Typography>
                      {!!check && (
                        <FormControl
                          sx={{
                            flexDirection: "row",
                            gap: "12px",
                            alignItems: "center",
                          }}
                        >
                          <FormLabel
                            id="demo-radio-buttons-group-label"
                            sx={{ fontWeight: "bold" }}
                          >
                            Tình trạng:
                          </FormLabel>
                          <RadioGroup
                            aria-labelledby="demo-radio-buttons-group-label"
                            value={productState?.qualityStatus}
                            name="radio-buttons-group"
                            sx={{ flexDirection: "row" }}
                            onChange={(e) =>
                              setProductState((prev) => ({
                                ...prev,
                                qualityStatus: e.target.value,
                              }))
                            }
                          >
                            <FormControlLabel
                              value={CHECK_RETURN_PROD_STATUS.normal}
                              control={<Radio />}
                              label="Bình thường"
                            />
                            <FormControlLabel
                              value={CHECK_RETURN_PROD_STATUS.damaged}
                              control={<Radio />}
                              label="Hư hỏng"
                            />
                            <FormControlLabel
                              value={CHECK_RETURN_PROD_STATUS.lostcode}
                              control={<Radio />}
                              label="Mất số serial"
                            />
                          </RadioGroup>
                        </FormControl>
                      )}
                      {productState?.qualityStatus === CHECK_RETURN_PROD_STATUS.lostcode && (
                        <TextField
                          label="Số lượng"
                          type="number"
                          fullWidth
                          size="small"
                          onChange={(e) =>
                            setProductState((prev) => ({
                              ...prev,
                              qty: Number(e.target.value),
                            }))
                          }
                        />
                      )}

                      <TextField
                        multiline
                        rows={2}
                        value={productState?.note || ""}
                        onChange={(e) =>
                          setProductState((prev) => ({
                            ...prev,
                            note: e.target.value,
                          }))
                        }
                        placeholder="Sản phẩm hư hỏng..."
                        fullWidth
                        label="Lý do"
                      />
                      <MallScanner
                        getValue={setCodeDetail}
                        value={codeDetail}
                        code="code"
                      />
                      <WaitListTable list={waitList} />
                    </Stack>
                  </Grid>
                </Grid>
              </Grid>
            </>
          ) : (
            <Grid
              xs={12}
              item
            >
              {!!check && (
                <Box sx={{ borderBottom: "1px solid var(--grey)", mb: 2 }}>
                  <FormControl>
                    <RadioGroup
                      row
                      value={exportTypePick}
                      onChange={(e) => setExportTypePick(e.target.value)}
                    >
                      <FormControlLabel
                        value={EXPORT_TYPE.WarehouseImport}
                        control={<Radio />}
                        label={EXPORT_TYPE_TRANSLATETRANSLATE[EXPORT_TYPE.WarehouseImport]}
                      />
                      <FormControlLabel
                        value={EXPORT_TYPE.DeliveredReceipt}
                        control={<Radio />}
                        disabled
                        label={EXPORT_TYPE_TRANSLATETRANSLATE[EXPORT_TYPE.DeliveredReceipt]}
                      />
                    </RadioGroup>
                  </FormControl>
                </Box>
              )}
              <Box
                sx={{
                  maxWidth: exportTypePick === EXPORT_TYPE.DeliveredReceipt ? PRINT_SIZE.width : "100%",
                  maxHeight: exportTypePick === EXPORT_TYPE.DeliveredReceipt ? PRINT_SIZE.height : "100%",
                  width: exportTypePick === EXPORT_TYPE.DeliveredReceipt ? "90%" : "100%",
                  m: "0 auto",
                }}
              >
                <DeliveredReceipt
                  isLoading={isLoading}
                  html={orderPreview}
                />
              </Box>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions sx={{ padding: "24px", flexDirection: "column" }}>
        {!isPrinted ? (
          <Button
            onClick={handleConfirm}
            fullWidth
            variant="contained"
          >
            Xác nhận
          </Button>
        ) : (
          isPrinted && (
            <Box display={{ width: "100%" }}>
              <Button
                disabled={printLoading}
                onClick={handlePrint}
                variant="contained"
                fullWidth
              >
                In phiếu giao hàng
              </Button>
            </Box>
          )
        )}
      </DialogActions>
    </Fragment>
  );
}
